import type { TileRect } from '@veriverse/game-logic';
import { TILE_SIZE } from '@veriverse/shared';
import type Phaser from 'phaser';
import { prefersReducedMotion } from '../state/settings.js';

/** Above the night shade and lamp glow, like the hover outline. */
const BUBBLE_DEPTH = 1660;
const BUBBLE_SIZE = 12;
/** Gap between the object's top edge and the bubble's tip, in pixels. */
const BUBBLE_GAP = 3;
const BOB_PX = 2;
const BOB_MS = 600;

const FILL = 0xfff6d6;
const BORDER = 0x3a2a12;
const KEY = 0xd4a017;

/**
 * Floating key prompt (spec §18): a 12×12 pixel-art bubble with a key cap hovering just above the
 * object the "E" prompt points at. It bobs gently, or stays still with reduced motion.
 */
export class PromptBubble {
  private readonly graphics: Phaser.GameObjects.Graphics;
  private tween: Phaser.Tweens.Tween | undefined;

  constructor(private readonly scene: Phaser.Scene) {
    this.graphics = scene.add.graphics().setDepth(BUBBLE_DEPTH).setVisible(false);
    // Origin at the bubble's bottom-centre tip, so positioning is just "above the object".
    const s = BUBBLE_SIZE;
    const g = this.graphics;
    g.fillStyle(BORDER, 1).fillRect(-s / 2, -s, s, s - 2); // outline
    g.fillRect(-1, -3, 3, 3); // tail
    g.fillStyle(FILL, 1).fillRect(-s / 2 + 1, -s + 1, s - 2, s - 4); // body
    g.fillRect(0, -3, 1, 1); // tail fill
    g.fillStyle(KEY, 1).fillRect(-2, -s + 3, 4, 4); // key cap
  }

  /** Shows the bubble over `footprint`, or hides it when there is no target. */
  show(footprint: TileRect | undefined): void {
    this.tween?.remove();
    this.tween = undefined;
    if (!footprint) {
      this.graphics.setVisible(false);
      return;
    }
    const x = Math.round((footprint.x + footprint.w / 2) * TILE_SIZE);
    const y = footprint.y * TILE_SIZE - BUBBLE_GAP;
    this.graphics.setPosition(x, y).setVisible(true);
    if (prefersReducedMotion()) return;
    this.tween = this.scene.tweens.add({
      targets: this.graphics,
      y: y - BOB_PX,
      duration: BOB_MS,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut',
    });
  }

  destroy(): void {
    this.tween?.remove();
    this.graphics.destroy();
  }
}
